import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaArrowLeft, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import SEO, { PageSEO, StructuredData } from '../../components/SEO';

const WeightLossTimeline = () => {
  const [unit, setUnit] = useState('metric');
  const [currentWeight, setCurrentWeight] = useState('');
  const [goalWeight, setGoalWeight] = useState('');
  const [weeklyLoss, setWeeklyLoss] = useState('0.5');
  const [result, setResult] = useState(null);

  const calculateTimeline = (e) => {
    e.preventDefault();

    let currentKg, goalKg;

    if (unit === 'metric') {
      currentKg = parseFloat(currentWeight);
      goalKg = parseFloat(goalWeight);
    } else {
      currentKg = parseFloat(currentWeight) * 0.453592;
      goalKg = parseFloat(goalWeight) * 0.453592;
    }

    const weightToLose = currentKg - goalKg;

    if (weightToLose <= 0) {
      setResult({ error: 'Goal weight must be less than current weight' });
      return;
    }

    const weeklyLossKg = parseFloat(weeklyLoss);
    const totalWeeks = Math.ceil(weightToLose / weeklyLossKg);
    const totalMonths = Math.round(totalWeeks / 4.33);
    const totalDays = totalWeeks * 7;

    // Calculate target date
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + totalDays);

    // Calculate milestones
    const milestones = [];
    const milestoneWeights = [0.25, 0.5, 0.75, 1];
    milestoneWeights.forEach((fraction) => {
      const weightLost = weightToLose * fraction;
      const weeksToMilestone = Math.ceil(weightLost / weeklyLossKg);
      const milestoneDate = new Date();
      milestoneDate.setDate(milestoneDate.getDate() + weeksToMilestone * 7);
      milestones.push({
        percent: fraction * 100,
        weight: (currentKg - weightLost).toFixed(1),
        date: milestoneDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        weeks: weeksToMilestone,
      });
    });

    // Daily calorie deficit needed
    const calorieDeficit = Math.round(weeklyLossKg * 7700 / 7);

    setResult({
      weightToLose: weightToLose.toFixed(1),
      totalWeeks,
      totalMonths,
      totalDays,
      targetDate: targetDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
      milestones,
      calorieDeficit,
      weeklyLossKg,
    });
  };

  const resetCalculator = () => {
    setCurrentWeight('');
    setGoalWeight('');
    setResult(null);
  };

  const timelineStructuredData = StructuredData.calculator(
    'Weight Loss Timeline Calculator',
    'Estimate how long it will take to reach your goal weight with milestones.',
    '/calculators/weight-loss-timeline'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.weightLossTimeline} structuredData={timelineStructuredData} />
      {/* Header */}
      <section className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/calculators" className="inline-flex items-center text-purple-100 hover:text-white mb-6 transition-colors">
            <FaArrowLeft className="mr-2" />
            All Calculators
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FaCalendarAlt className="text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Weight Loss Timeline</h1>
              <p className="text-purple-100 mt-1">See how long it will take to reach your goal</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Enter Your Details</h2>

            {/* Unit Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setUnit('metric')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'metric' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Metric (kg)
              </button>
              <button
                type="button"
                onClick={() => setUnit('imperial')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'imperial' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Imperial (lbs)
              </button>
            </div>

            <form onSubmit={calculateTimeline} className="space-y-5">
              {/* Current Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Weight ({unit === 'metric' ? 'kg' : 'lbs'})
                </label>
                <input
                  type="number"
                  value={currentWeight}
                  onChange={(e) => setCurrentWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'e.g., 85' : 'e.g., 187'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                  min="30"
                  step="0.1"
                />
              </div>

              {/* Goal Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Goal Weight ({unit === 'metric' ? 'kg' : 'lbs'})
                </label>
                <input
                  type="number"
                  value={goalWeight}
                  onChange={(e) => setGoalWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'e.g., 70' : 'e.g., 154'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                  min="30"
                  step="0.1"
                />
              </div>

              {/* Weekly Weight Loss Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekly Weight Loss Rate
                </label>
                <select
                  value={weeklyLoss}
                  onChange={(e) => setWeeklyLoss(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="0.25">Slow (0.25 kg/week) - Easiest</option>
                  <option value="0.5">Moderate (0.5 kg/week) - Recommended</option>
                  <option value="0.75">Fast (0.75 kg/week) - Challenging</option>
                  <option value="1">Aggressive (1 kg/week) - Very Hard</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 px-6 rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-colors font-semibold"
                >
                  Calculate Timeline
                </button>
                {result && (
                  <button
                    type="button"
                    onClick={resetCalculator}
                    className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Timeline</h2>

            {result ? (
              result.error ? (
                <div className="p-4 bg-red-50 rounded-xl text-red-700">
                  <FaExclamationTriangle className="inline mr-2" />
                  {result.error}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-6 text-white text-center">
                    <div className="text-purple-100 text-sm mb-1">You'll reach your goal by</div>
                    <div className="text-2xl font-bold mb-2">{result.targetDate}</div>
                    <div className="text-purple-100">
                      {result.totalWeeks} weeks ({result.totalMonths} months)
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900">{result.weightToLose}</div>
                      <div className="text-sm text-gray-600">kg to lose</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900">{result.calorieDeficit}</div>
                      <div className="text-sm text-gray-600">cal deficit/day</div>
                    </div>
                  </div>

                  {/* Milestones */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Milestones</h3>
                    <div className="space-y-3">
                      {result.milestones.map((milestone, index) => (
                        <div key={index} className="flex items-center bg-gray-50 rounded-xl p-3">
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold mr-4">
                            {milestone.percent}%
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{milestone.weight} kg</div>
                            <div className="text-sm text-gray-600">{milestone.date}</div>
                          </div>
                          <div className="text-sm text-gray-500">
                            Week {milestone.weeks}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <Link
                    to="/pricing"
                    className="block w-full text-center bg-accent-500 text-white py-3 rounded-xl hover:bg-accent-600 transition-colors font-semibold"
                  >
                    Start Your Journey Today
                  </Link>
                </div>
              )
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FaCalendarAlt className="text-5xl mx-auto mb-4 text-gray-300" />
                <p>Enter your current and goal weight to see your timeline</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <FaInfoCircle className="text-purple-600 text-xl" />
            <h3 className="text-lg font-bold text-gray-900">Safe Weight Loss Guidelines</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Recommended Rate</h4>
              <p className="text-sm text-gray-600">
                Most health experts recommend losing 0.5-1 kg (1-2 lbs) per week for sustainable results.
                Faster weight loss can lead to muscle loss and nutritional deficiencies.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Calorie Deficit</h4>
              <p className="text-sm text-gray-600">
                To lose 0.5 kg per week, you need a daily deficit of about 550 calories.
                This can be achieved through diet, exercise, or a combination of both.
              </p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-yellow-50 rounded-xl">
            <div className="flex items-center gap-2 text-yellow-700 font-medium mb-1">
              <FaExclamationTriangle />
              <span>Important Note</span>
            </div>
            <p className="text-sm text-gray-600">
              Weight loss is rarely linear. You may experience plateaus, water weight fluctuations, and varying rates of loss.
              Focus on consistency rather than daily weigh-ins.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeightLossTimeline;
